import { useEffect, useRef, useState } from 'react';
import {
  SparklesIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import {
  speciesApi,
  type CompletenessSummary,
  type EnrichmentJob,
  type EnrichmentStatus,
} from '../../api/species';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCompleted?: () => void;
}

const isActive = (job: EnrichmentJob | null | undefined) =>
  !!job && (job.status === 'PENDING' || job.status === 'PROCESSING');

export default function SpeciesEnrichmentModal({ isOpen, onClose, onCompleted }: Props) {
  const [summary, setSummary] = useState<CompletenessSummary | null>(null);
  const [status, setStatus] = useState<EnrichmentStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [onlyIncomplete, setOnlyIncomplete] = useState(true);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const job = status?.currentJob || status?.lastJob || null;

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        speciesApi.getCompleteness(),
        speciesApi.getEnrichmentStatus(),
      ]);
      setSummary(c.data);
      setStatus(s.data);
    } catch (err) {
      console.error('Failed to load enrichment data', err);
      toast.error('Failed to load completeness data');
    } finally {
      setLoading(false);
    }
  };

  const refreshStatus = async () => {
    try {
      const s = await speciesApi.getEnrichmentStatus();
      setStatus(s.data);
      return s.data;
    } catch {
      return null;
    }
  };

  // Load on open; clear polling on close/unmount.
  useEffect(() => {
    if (isOpen) {
      void loadAll();
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Poll while a job is running.
  useEffect(() => {
    if (!isOpen) return;
    const running = isActive(status?.currentJob);
    if (running && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const s = await refreshStatus();
        if (s && !isActive(s.currentJob)) {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
          await loadAll();
          onCompleted?.();
          const finished = s.lastJob;
          if (finished?.status === 'COMPLETED') {
            toast.success(
              `Enrichment complete — ${finished.updated} updated, ${finished.fieldsFilled} fields filled`,
            );
          } else if (finished?.status === 'FAILED') {
            toast.error(finished.error || 'Enrichment failed');
          }
        }
      }, 2500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, status?.currentJob?.status]);

  const handleRun = async () => {
    setStarting(true);
    try {
      await speciesApi.runEnrichment({ force: !onlyIncomplete });
      toast.success('AI completion started');
      await refreshStatus();
    } catch (err) {
      console.error('Failed to start enrichment', err);
      toast.error('Failed to start AI completion');
    } finally {
      setStarting(false);
    }
  };

  const running = isActive(status?.currentJob);
  const progressPct =
    job && job.total > 0 ? Math.round((job.processed / job.total) * 100) : running ? 5 : 0;

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-3xl" zIndexClass="z-20">
      <BrandModalHeader
        title={
          <span className="inline-flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-100">
              <SparklesIcon className="h-6 w-6 text-cyan-600" />
            </span>
            AI Data Completion
          </span>
        }
        subtitle="Fill in missing fields across your species catalog using the same AI sources used at species creation."
        onClose={onClose}
      />
      <BrandModalBody>

                  {/* AI not configured warning */}
                  {status && !status.aiConfigured && (
                    <div className="mt-4 flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
                      <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
                      <span>
                        AI enrichment is not configured. Set <code>OPENAI_API_KEY</code> on
                        the API to enable automatic completion.
                      </span>
                    </div>
                  )}

                  {/* Completeness stats */}
                  <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <StatCard label="Species" value={summary?.totalSpecies ?? '—'} />
                    <StatCard
                      label="Complete"
                      value={summary?.completeSpecies ?? '—'}
                      tone="success"
                    />
                    <StatCard
                      label="Incomplete"
                      value={summary?.incompleteSpecies ?? '—'}
                      tone="warning"
                    />
                    <StatCard
                      label="Avg. Complete"
                      value={summary ? `${summary.averageCompleteness}%` : '—'}
                    />
                  </div>

                  {/* Top field gaps */}
                  {summary && summary.fieldGaps.length > 0 && (
                    <div className="mt-5">
                      <h4 className="text-sm font-medium text-slate-900">Biggest data gaps</h4>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {summary.fieldGaps.slice(0, 8).map((g) => (
                          <span
                            key={g.field}
                            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700"
                          >
                            {g.label}
                            <span className="font-semibold text-slate-900">{g.missing}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Progress */}
                  {job && (running || job.status === 'COMPLETED' || job.status === 'FAILED') && (
                    <div className="mt-5 rounded-lg border border-slate-200 p-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-slate-900">
                          {running
                            ? 'Completing species data…'
                            : job.status === 'COMPLETED'
                            ? 'Last run complete'
                            : 'Last run failed'}
                        </span>
                        <span className="text-slate-500">
                          {job.processed}/{job.total}
                        </span>
                      </div>
                      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                        <div
                          className={`h-full rounded-full transition-all ${
                            job.status === 'FAILED' ? 'bg-red-500' : 'bg-cyan-500'
                          }`}
                          style={{ width: `${progressPct}%` }}
                        />
                      </div>
                      {running && job.currentSpecies && (
                        <p className="mt-2 text-xs text-slate-500">
                          Processing: <span className="italic">{job.currentSpecies}</span>
                        </p>
                      )}
                      {!running && (
                        <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-600">
                          <span className="text-emerald-600">{job.updated} updated</span>
                          <span>{job.skipped} skipped</span>
                          {job.failed > 0 && <span className="text-red-600">{job.failed} failed</span>}
                          <span className="text-slate-900">{job.fieldsFilled} fields filled</span>
                        </div>
                      )}
                      {job.error && <p className="mt-2 text-xs text-red-600">{job.error}</p>}
                    </div>
                  )}

                  {/* Run options */}
                  <div className="mt-5 border-t border-slate-200 pt-4">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={onlyIncomplete}
                        onChange={(e) => setOnlyIncomplete(e.target.checked)}
                        className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                      />
                      Only fill missing fields (recommended)
                    </label>
                  </div>

                  {summary?.incompleteSpecies === 0 && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-emerald-600">
                      <CheckCircleIcon className="h-5 w-5" />
                      All species records are complete.
                    </div>
                  )}
      </BrandModalBody>
      <BrandModalFooter>
        <button
          type="button"
          onClick={onClose}
          className="btn btn-secondary text-sm"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={loadAll}
          disabled={loading}
          className="btn btn-secondary text-sm inline-flex items-center disabled:opacity-50"
        >
          <ArrowPathIcon className={`mr-1.5 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
        <button
          type="button"
          onClick={handleRun}
          disabled={
            starting || running || !status?.aiConfigured || summary?.incompleteSpecies === 0
          }
          className="btn btn-primary text-sm inline-flex items-center focus-pop disabled:opacity-50"
        >
          {running ? (
            <>
              <ArrowPathIcon className="mr-1.5 h-5 w-5 animate-spin" />
              Running…
            </>
          ) : (
            <>
              <SparklesIcon className="mr-1.5 h-5 w-5" />
              {onlyIncomplete ? 'Complete Missing Data' : 'Refresh All Data'}
            </>
          )}
        </button>
      </BrandModalFooter>
    </BrandModal>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | number;
  tone?: 'success' | 'warning';
}) {
  const valueColor =
    tone === 'success' ? 'text-emerald-600' : tone === 'warning' ? 'text-amber-600' : 'text-slate-900';
  return (
    <div className="rounded-lg bg-slate-50 p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1 text-xl font-semibold ${valueColor}`}>{value}</p>
    </div>
  );
}
